import isEmpty from 'lodash/isEmpty';
import Cookies from '../Cookies/Cookies';

/* istanbul ignore next - JSDOM does not support and this is development tool */
class LocalStorage {
    static yodaStoreKey = '__yoda';

    constructor(isLocalStorage) {
        if (this.isLocalStorageSupported()) {
            this.localStorageSupport = true;
            this.storage = isLocalStorage ? localStorage : sessionStorage;
        } else {
            this.localStorageSupport = false;
        }
    }

    isLocalStorageSupported() {
        const testData = 'testData';
        try {
            localStorage.setItem(testData, testData);
            localStorage.removeItem(testData);
            return true;
        } catch (e) {
            return false;
        }
    }

    // storeWithKey - Pass this parameter as true if you want to store data with the same key.
    // expiryInMins - Pass the expiry time in minutes which will be getting from preferences.
    setData(key, value, storeWithKey, domain, fallbackEnabled = true, expiryInMins) {
        // If it is server side rendering component, simply return null as localStorage object is not available.
        if (__SERVER__) {
            return null;
        }
        let isSuccess = false;

        if (this.localStorageSupport) {
            if (storeWithKey) {
                if (typeof value !== 'object') {
                    this.storage.setItem(key, value);
                } else {
                    this.storage.setItem(key, JSON.stringify(value));
                }
            } else {
                const yodaStore = this.getYodaStore();
                yodaStore[key] = value;

                this.storage.setItem(LocalStorage.yodaStoreKey, JSON.stringify(yodaStore));
            }
            if (expiryInMins) {
                const expiryTime = Date.now() + expiryInMins * 60 * 1000;
                this.storage.setItem(`${key}__expiryMins_`, expiryTime);
            }

            isSuccess = true;
        } else {
            if (fallbackEnabled) {
                const days = expiryInMins ? expiryInMins / 60 / 24 : '';
                Cookies.save(key, JSON.stringify(value), days, domain);
            }

            isSuccess = false;

            console.error('localStorage is not available!');
        }

        return isSuccess;
    }

    getYodaStore() {
        if (this.localStorageSupport) {
            const yodaStore = this.storage.getItem(LocalStorage.yodaStoreKey);
            return yodaStore !== 'null' && !isEmpty(yodaStore) ? JSON.parse(yodaStore) : {};
        }
        return {};
    }

    checkForExpiredData = (key, ttlValue, value) => {
        const expiryTime = Number(ttlValue);
        const currTime = Date.now();
        if (expiryTime < currTime) {
            const getExpiryKey = key.lastIndexOf('__expiryMins_');
            const removeExpiredKey = key.slice(0, getExpiryKey);
            this.storage.removeItem(key); // clear the expiryInMins timestamp
            this.storage.removeItem(removeExpiredKey); // clear the cached localstorage data after specified time
            value = null;
        }
        return value;
    };

    getData(key, retrieveWithKey) {
        // If it is server side rendering component, simply return null as localStorage object is not available.
        if (__SERVER__) {
            return null;
        }
        let value = null;

        if (this.localStorageSupport) {
            if (retrieveWithKey) {
                value = this.storage.getItem(key);
            } else {
                const localStorageMap = this.getYodaStore();
                value = !isEmpty(localStorageMap) ? localStorageMap[key] : null;
            }
            // check for expiryInMins Timestamp key
            const ttlKey = `${key}__expiryMins_`;
            const ttlValue = this.storage.getItem(ttlKey);
            value = ttlValue ? this.checkForExpiredData(ttlKey, ttlValue, value) : value;
        } else {
            value = Cookies.load(key);
            console.error('localStorage is not available!');
        }

        value = value && value === 'null' ? null : value;

        return value || null;
    }

    removeData(key, withKey, domain) {
        if (this.localStorageSupport) {
            if (withKey) {
                this.storage.removeItem(key);
            } else {
                const yodaStore = this.getYodaStore();
                if (yodaStore[key]) {
                    delete yodaStore[key];
                    this.storage.setItem(LocalStorage.yodaStoreKey, JSON.stringify(yodaStore));
                }
            }
        } else {
            Cookies.remove(key, domain);
        }
    }
}

export default new LocalStorage(true);
export const SessionStorage = new LocalStorage();
